// ============================================================================
// Why a service line has not started — read back from the line's own note.
//
// Item 847. "Not started yet" keeps the line's signed price, clears its start
// date and puts it in the existing Scheduled status; the reason is APPENDED to
// the note the line already has, because nothing new was to be added for it
// (no column, no table). mark_service_line_not_started() writes it as
//
//   <YYYY-MM-DD> not started: <reason> (<who>)
//
// and this reads the latest one back so the card can show it under the line.
//
// ⛔ A line marked by hand before the button existed carries the same fact in
// the person's own words (Houston Physicians Hospital's day porter: "per Ben:
// day porter line NOT started. Contingent on client census…"). That segment is
// shown as written rather than hidden for not matching the shape.
// ============================================================================

export interface NotStartedReason {
  /** What we are waiting on, as the account manager wrote it. */
  reason: string;
  /** Who marked it, when the action recorded it. */
  who: string | null;
  /** The day it was marked (YYYY-MM-DD), when the action recorded it. */
  on: string | null;
}

const SEGMENT = ' | ';
const WRITTEN_BY_THE_ACTION = /^(\d{4}-\d{2}-\d{2}) not started: ([\s\S]*?)(?: \(([^()]*)\))?$/;

export function notStartedReason(notes: string | null | undefined): NotStartedReason | null {
  const text = (notes ?? '').trim();
  if (!text) return null;

  // The action appends, so the newest mark is the LAST one. A reason may itself
  // contain the separator, so the action's own mark is found by its date-led
  // phrase rather than by splitting first.
  const marks = [...text.matchAll(/(?:^| \| )(\d{4}-\d{2}-\d{2} not started: )/g)];
  const last = marks[marks.length - 1];
  if (last && last.index !== undefined) {
    const start = last.index + (last[0].startsWith(SEGMENT) ? SEGMENT.length : 0);
    let segment = text.slice(start).trim();
    // A later note appended after the mark ends where the mark's "(who)" closes.
    const close = segment.indexOf(`)${SEGMENT}`);
    if (close >= 0) segment = segment.slice(0, close + 1);
    const m = WRITTEN_BY_THE_ACTION.exec(segment);
    if (m) return { reason: m[2].trim(), who: m[3]?.trim() || null, on: m[1] };
  }

  // A hand-written mark: the latest segment that says so in words.
  const segments = text.split(SEGMENT).map((s) => s.trim()).filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    if (/not started/i.test(segments[i])) return { reason: segments[i], who: null, on: null };
  }
  return null;
}
